import { useLanguage } from '@/app/providers';
import { CloseIcon, IconBlock } from '@/assets/images';
import { AppIcon } from '@/components';
import { RequestChatBanner } from '@/features/invite/ui/RequestChatBanner';
import { Username, handleToggleBlockClick } from '@/features/users';
import { getUserIdFromJid } from '@/shared/utils';
import PropTypes from 'prop-types';
import { useChatInfo } from '../../sub-features/chat-info/viewmodel/useChatInfo';
import { isGroupChat } from '../../utils';
import { useChatRequest, useConversationFooter, useEditMessage } from '../../viewmodels';
import { useReplyMessage } from '../../viewmodels/useReplyMessage';
import ChatInput from './ChatInput';
import './ConversationScreen.scss';
import ReplyMessageFooter from './ReplyMessageFooter';

/**
 * Pure rendering component.
 * All selection mode action logic + send handled by useConversationFooter().
 */
const ConversationFooter = ({ activeChatJid }) => {
   const { t } = useLanguage();
   const { selectionCount, messageActions, handleSend, handleResetSelection, amIParticipant } =
      useConversationFooter(activeChatJid);
   const { isBlockedByMe, isDeleted } = useChatInfo(activeChatJid);
   const activeUserId = getUserIdFromJid(activeChatJid);
   const { replyMessage } = useReplyMessage({ activeChatJid });
   const { editMessageId } = useEditMessage({ activeChatJid });
   const { isReceiver, canChat } = useChatRequest(activeChatJid);
   

   // Selection mode — show action bar
   if (selectionCount > 0) {
      return (
         <div className="conversation-footer selection-mode">
            <div className="selection-action-bar ct-flex ct-aiCenter ct-jcBetween">
               <div className="ct-flex ct-aiCenter ct-gap8 ct-selectedActionLeft">
                  <button
                     className="ct-flex selection-close-btn"
                     onClick={handleResetSelection}
                     aria-label="Clear selection"
                  >
                     <i className="ct-icon ct-icon-close">
                        <CloseIcon />
                     </i>
                  </button>
                  <span className="selection-count">{selectionCount} selected</span>
               </div>
               <div className="selection-actions ct-flex ct-aiCenter ct-jcCenter ct-gap12">
                  {Object.values(messageActions)
                     .filter(action => action.show !== false)
                     .map(action => (
                        <button
                           key={action.id}
                           className="ct-flex selection-action-btn"
                           onClick={action.handler}
                           aria-label={action.id}
                        >
                           <AppIcon icon={action.icon} />
                        </button>
                     ))}
               </div>
            </div>
         </div>
      );
   }

   if (!amIParticipant && isGroupChat(activeChatJid)) {
      return (
         <footer className="conversation-footer blocked-footer ct-flexCenter">
            <div className="blocked-message ct-flex ct-aiCenter ct-gap4">{t('toast.NOT_A_PARTICIPANT')}</div>
         </footer>
      );
   }

   if (isBlockedByMe) {
      return (
         <footer className="conversation-footer blocked-footer ct-flexCenter">
            <div className="blocked-message ct-flex ct-aiCenter ct-gap4">
               <IconBlock />
               <span>{t('chat.YOU_HAVE_BLOCKED')} </span>
               <Username userId={activeUserId} />
               <button
                  className="unblock-link-btn ct-noBorder ct-noPadding ct-cursor"
                  onClick={e => handleToggleBlockClick({ e, jid: activeChatJid })}
               >
                  {t('chat.UNBLOCK')}
               </button>
            </div>
         </footer>
      );
   }

   if (isDeleted) {
      return (
         <footer className="conversation-footer blocked-footer ct-flexCenter">
            <div className="blocked-message ct-flex ct-aiCenter ct-gap4">
               <IconBlock />
               <span>{t('chat.USER_DELETED_ACCOUNT')}</span>
            </div>
         </footer>
      );
   }

   if (isReceiver) {
      return (
         <footer className="conversation-footer request-banner-footer">
            <RequestChatBanner chatJid={activeChatJid} />
         </footer>
      );
   }

   if (canChat) {
      return (
         <footer className="conversation-footer ct-flex ct-fcol">
            <ChatInput activeChatJid={activeChatJid} onSend={handleSend} hasReply={!!replyMessage || !!editMessageId}>
               {replyMessage && <ReplyMessageFooter activeChatJid={activeChatJid} replyMessage={replyMessage} />}
               {editMessageId && <ReplyMessageFooter activeChatJid={activeChatJid} isEdit={true} />}
            </ChatInput>
         </footer>
      );
   }

   return null;
};

export default ConversationFooter;

ConversationFooter.propTypes = {
   activeChatJid: PropTypes.string,
};
